import { deepEqual } from '@/lib/utils'
import { SectionWithHash } from '../editorblocks/sectionWithHash'
import { SECTION_TYPE_LABELS } from '../constants'
import { stripComparableSectionFields } from './transformToComparable'

const displayName = (section: SectionWithHash): string =>
    SECTION_TYPE_LABELS[section.type] ?? section.type

const toComparable = (section: SectionWithHash) => {
    const { hash, ...rest } = stripComparableSectionFields(
        section,
    ) as SectionWithHash
    return rest
}

/** Produces a short human-readable summary of what changed between two section arrays, e.g. "Reordered sections; Edited 1 section". */
export function summarizeSectionsDiff(
    before: SectionWithHash[],
    after: SectionWithHash[],
): string {
    const beforeByHash = new Map(before.map((s) => [s.hash, s]))
    const afterByHash = new Map(after.map((s) => [s.hash, s]))

    const added = after.filter((s) => !beforeByHash.has(s.hash))
    const removed = before.filter((s) => !afterByHash.has(s.hash))
    const modified = after.filter((s) => {
        const prev = beforeByHash.get(s.hash)
        return !!prev && !deepEqual(toComparable(prev), toComparable(s))
    })

    const commonBeforeOrder = before
        .map((s) => s.hash)
        .filter((hash) => afterByHash.has(hash))
    const commonAfterOrder = after
        .map((s) => s.hash)
        .filter((hash) => beforeByHash.has(hash))
    const reordered =
        commonBeforeOrder.length > 1 &&
        !commonBeforeOrder.every((hash, i) => hash === commonAfterOrder[i])

    const parts: string[] = []
    if (added.length) {
        parts.push(
            added.length === 1
                ? `Added ${displayName(added[0])}`
                : `Added ${added.length} sections`,
        )
    }
    if (removed.length) {
        parts.push(
            removed.length === 1
                ? `Removed ${displayName(removed[0])}`
                : `Removed ${removed.length} sections`,
        )
    }
    if (modified.length) {
        parts.push(
            modified.length === 1
                ? `Edited ${displayName(modified[0])}`
                : `Edited ${modified.length} sections`,
        )
    }
    if (reordered) {
        parts.push('Reordered sections')
    }

    return parts.length ? parts.join('; ') : 'No changes'
}
